/**
 * OrderSummary Component
 * Checkout summary with line items, legal acceptance, and totals
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Checkbox,
  FormControlLabel,
  Link,
  Stack,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import { useCartItemsWithDetails, useCartStore } from '../../store/cart';
import { formatPrice } from '../../lib/catalog';

interface OrderSummaryProps {
  termsAccepted: boolean;
  onTermsChange: (accepted: boolean) => void;
  email?: string;
  company?: string;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  termsAccepted,
  onTermsChange,
  email,
  company,
}) => {
  const theme = useTheme();
  const items = useCartItemsWithDetails();
  const { hasSubscription } = useCartStore();

  // Calculate totals
  const oneTimeTotal = items
    .filter(i => i.sku.type !== 'subscription')
    .reduce((sum, i) => sum + i.subtotal, 0);

  const recurringTotal = items
    .filter(i => i.sku.type === 'subscription')
    .reduce((sum, i) => sum + i.subtotal, 0);

  const total = oneTimeTotal + recurringTotal;

  const getTypeLabel = (type: string): string => {
    switch (type) {
      case 'subscription':
        return 'Monthly';
      case 'metered':
        return 'Per Hour';
      default:
        return 'One-time';
    }
  };

  return (
    <Card
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
      }}
    >
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Order Summary
        </Typography>

        {/* Customer info if provided */}
        {(email || company) && (
          <Box
            sx={{
              bgcolor: alpha(theme.palette.info.main, 0.1),
              borderRadius: 1,
              p: 1.5,
              mb: 2,
            }}
          >
            {email && (
              <Typography variant="body2">
                <strong>Email:</strong> {email}
              </Typography>
            )}
            {company && (
              <Typography variant="body2">
                <strong>Company:</strong> {company}
              </Typography>
            )}
          </Box>
        )}

        {/* Line items */}
        <List disablePadding>
          {items.map((item, index) => (
            <React.Fragment key={item.skuId}>
              <ListItem sx={{ px: 0, py: 1 }}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1">{item.sku.name}</Typography>
                      <Chip
                        label={getTypeLabel(item.sku.type)}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                    </Box>
                  }
                  secondary={
                    item.qty > 1
                      ? `${item.qty} × ${formatPrice(item.sku.unitAmount)}`
                      : item.sku.type === 'metered'
                      ? `${item.qty} hour${item.qty > 1 ? 's' : ''}`
                      : null
                  }
                />
                <Typography variant="body1" fontWeight="medium">
                  {formatPrice(item.subtotal)}
                  {item.sku.type === 'subscription' && (
                    <Typography component="span" variant="caption" color="text.secondary">
                      /mo
                    </Typography>
                  )}
                </Typography>
              </ListItem>
              {index < items.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />

        {/* Totals */}
        <Stack spacing={1}>
          {oneTimeTotal > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                One-time charges:
              </Typography>
              <Typography variant="body2">{formatPrice(oneTimeTotal)}</Typography>
            </Box>
          )}

          {hasSubscription() && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Monthly recurring:
              </Typography>
              <Typography variant="body2">{formatPrice(recurringTotal)}/mo</Typography>
            </Box>
          )}

          {/* Tax placeholder */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              Tax:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Calculated at checkout
            </Typography>
          </Box>

          <Divider sx={{ my: 1 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h6">Total Due Today:</Typography>
            <Typography variant="h6" color="primary" fontWeight="bold">
              {formatPrice(total)}
            </Typography>
          </Box>

          {hasSubscription() && (
            <Typography variant="caption" color="text.secondary" textAlign="right">
              Then {formatPrice(recurringTotal)}/month starting next billing cycle
            </Typography>
          )}
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Legal acceptance */}
        <FormControlLabel
          control={
            <Checkbox
              checked={termsAccepted}
              onChange={(e) => onTermsChange(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              I agree to the REDEX{' '}
              <Link href="/terms.html" target="_blank" rel="noopener">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link href="/privacy.html" target="_blank" rel="noopener">
                Privacy Policy
              </Link>
              . Deliverables & SLAs are defined in the SOW and Capabilities Matrix.
            </Typography>
          }
          sx={{ alignItems: 'flex-start', mt: 1 }}
        />
      </CardContent>
    </Card>
  );
};

export default OrderSummary;
